import { detectContentType } from "@/lib/utils";
import { Outline, Section } from "@/types/article";
import { PostsDetail } from "@/types/pocketbase-types";
import { TElement } from "@udecode/plate";
import { deserializeMd } from "@udecode/plate-markdown";
import { TPlateEditor } from "@udecode/plate/react";

/**
 * Creates an array of TElements directly from an OutlineResponse
 * This bypasses the HTML generation and deserialization steps
 * @param outline The OutlineResponse object
 * @returns Array of TElements ready to be used by the editor
 */
export const createElementsFromOutline = (
  postDetail: PostsDetail,
  editor: TPlateEditor,
): TElement[] => {
  const elements: TElement[] = [];

  const outline = postDetail.outline as Outline;

  // Create elements from markdown content first (for regenerated content)
  if (
    postDetail.content &&
    postDetail.content.length > 0 &&
    detectContentType(postDetail.content) === "markdown"
  ) {
    console.log("Creating outlineState from content markdown");
    const contentElements = deserializeMd(editor, postDetail.content);
    elements.push(...contentElements);
    return elements;
  }

  // If no markdown content, fall back to outline-based generation
  if (!outline) return elements;

  // Introduction (paragraphs)
  const introElements = deserializeMd(editor, outline.introduction);
  elements.push(...introElements);

  // Create elements from sections
  if (outline.sections) {
    outline.sections.forEach((section: Section) => {
      // Section title (h2)
      elements.push({
        type: "h2",
        children: [{ text: section.title }],
      });

      // Section image (if available)
      if (section.image && section.image.url) {
        elements.push({
          type: "img",
          url: section.image.url,
          alt: section.image.alt || section.title,
          caption: section.title,
          children: [{ text: "" }],
        });
      }

      // Section content
      if (section.content) {
        // Remove the section title if it appears at the beginning of the content
        const contentWithoutTitle = section.content.replace(
          new RegExp(
            `^#{1,3}\\s*${section.title.replace(
              /[.*+?^${}()|[\]\\]/g,
              "\\$&",
            )}\\s*$`,
            "im",
          ),
          "",
        );

        // Use deserializeMd function to convert markdown to TElements
        const contentElements = deserializeMd(editor, contentWithoutTitle);
        elements.push(...contentElements);
      } else if (section.points && section.points.length > 0) {
        // Convert points to markdown list and deserialize
        const pointsMarkdown = section.points
          .map((point) => `- ${point}`)
          .join("\n");
        const listElements = deserializeMd(editor, pointsMarkdown);
        elements.push(...listElements);
      }
    });
  }

  // Add Conclusion
  if (outline.conclusion) {
    // Add a heading for the conclusion
    elements.push({
      type: "h2",
      children: [{ text: "Conclusion" }],
    });

    // Use deserializeMd function for conclusion
    const conclusionElements = deserializeMd(editor, outline.conclusion);
    elements.push(...conclusionElements);
  }

  // Add Call to Action
  if (outline.cta) {
    // Use deserializeMd function for CTA
    const ctaElements = deserializeMd(editor, outline.cta);
    elements.push(...ctaElements);
  }

  return elements;
};
